import React, { PropsWithChildren, ReactNode } from "react";
import styled, { css } from "styled-components";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import NewLookBackLink from "features/settings/NewLook/NewLookBackLink.tsx";

interface NewLookFormPanelProps {
  title: ReactNode;
  description?: ReactNode;
  onBack?: () => void;
  backLabel?: ReactNode;
  /** Wider panel for step flows (2FA). */
  wide?: boolean;
}

/**
 * A focused settings form (change password, change name, connect email, 2FA)
 * in the new look: a back link, then one panel with its title in a hairline
 * header (description as meta) and the form underneath.
 */
const NewLookFormPanel = ({
  title,
  description,
  onBack,
  backLabel,
  wide,
  children,
}: PropsWithChildren<NewLookFormPanelProps>) => (
  <Wrapper $wide={wide}>
    {onBack && <NewLookBackLink onClick={onBack}>{backLabel}</NewLookBackLink>}
    <Panel>
      <Head>
        <Title>{title}</Title>
        {description && <Description>{description}</Description>}
      </Head>
      <Body>{children}</Body>
    </Panel>
  </Wrapper>
);

export default NewLookFormPanel;

/**
 * The form's actions, bottom-right on a hairline (full width on phones).
 */
export const NewLookFormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
  padding-top: 20px;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  .ant-btn {
    min-width: 140px;
  }
  @media (max-width: 500px) {
    padding-top: 16px;
    .ant-btn {
      flex: 1;
    }
  }
`;

const Wrapper = styled.div<{ $wide?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  max-width: 560px;
  ${(p) =>
    p.$wide &&
    css`
      max-width: 720px;
    `}
`;
const Panel = styled.section`
  width: 100%;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  border: ${(p) => p.theme.borders.panel};
  border-radius: ${(p) => p.theme.radii.card}px;
  box-shadow: ${(p) => p.theme.shadows.panel};
`;
const Head = styled.header`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  min-height: 60px;
  padding: 12px 24px;
  border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
const Title = styled.h2`
  margin: 0;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Description = styled.div`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Body = styled.div`
  padding: 20px 24px 24px;
  /* One 16px rhythm between fields. */
  .ant-form-item {
    margin-bottom: 16px;
  }
`;
